import { useCallback, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { Badge } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { ActionItemStatus, patchProviderDetails } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { providerQueryKeys } from '@uplift/queries';
import {
  BaseDashboardActionItem, CancelButton, LightChip, Loader,
  ModalActions, ModalFullPage, ModalFullPageContent, ModalTitle, SubmitButton,
} from '@uplift/ui-v2';
import { useQueryClient } from '@tanstack/react-query';
import { Formik } from 'formik';

import { getChipColor } from '../../helpers';
import { useSharedStyles } from '../../styles';
import { PayorEnrollmentFormContent } from '../../../onboarding-page/pages/payor-enrollment/PayorEnrollmentFormContent';
import {
  PayorEnrollmentForm,
  payorEnrollmentInitialValues,
  payorEnrollmentValidation,
} from '../../../onboarding-page/pages/payor-enrollment/validation';

export type SetUpPayorEnrollmentProps = {
  status: ActionItemStatus;
};

export const SetUpPayorEnrollment: React.VFC<SetUpPayorEnrollmentProps> = ({ status }) => {
  const [ isModalOpen, setIsModalOpen ] = useState(false);
  const handleCloseModal = () => setIsModalOpen(false);
  const l10n = useL10n();
  const { classes: sharedClasses } = useSharedStyles();
  const me = useCurrentuser();
  const queryClient = useQueryClient();

  const handleSubmit = useCallback(async (values: PayorEnrollmentForm) => {
    await patchProviderDetails(me.id, {
      ...(values.npi ? { npi: values.npi } : {}),
      caqhId: values.caqhId,
      caqhUsername: values.caqhUsername,
      caqhPassword: values.caqhPassword,
    });
    await queryClient.invalidateQueries(providerQueryKeys.activationEvents(me.id));
    handleCloseModal();
  }, [ me?.id, queryClient, handleCloseModal ]);

  if (status === ActionItemStatus.done) {
    return (
      <Box className={sharedClasses.progressSummaryStepWithChip}>
        <Box display="flex" flexDirection="column">
          <Typography variant="h5">{l10n.dashboard.actionItems.payorEnrollment.progressLabel}</Typography>
        </Box>
        <LightChip label={l10n.dashboard.actionItems.status[status]} color={getChipColor(status)} size="small" />
      </Box>
    );
  }

  if (status !== ActionItemStatus.todo) {
    return null;
  }

  return (
    <>
      <BaseDashboardActionItem
        title={l10n.dashboard.actionItems.payorEnrollment.title}
        body={l10n.dashboard.actionItems.payorEnrollment.subtitle}
        bodyClassName={sharedClasses.actionItemSubtitle}
        color="primary"
        icon={<Badge />}
        action={{
          handleClick: () => setIsModalOpen(true),
          text: l10n.dashboard.actionItems.payorEnrollment.buttonText,
        }}
      />
      {isModalOpen && (
        <PayorEnrollmentModal
          onSubmit={handleSubmit}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

type PayorEnrollmentModalProps = {
  onSubmit: (values: PayorEnrollmentForm) => Promise<void>;
  onClose: () => void;
};

const PayorEnrollmentModal: React.VFC<PayorEnrollmentModalProps> = ({ onSubmit, onClose }) => {
  const l10n = useL10n();
  const { classes } = useModalStyles();
  const [ submitError, setSubmitError ] = useState<string | null>(null);

  return (
    <Formik
      initialValues={payorEnrollmentInitialValues}
      validationSchema={payorEnrollmentValidation}
      validateOnMount
      onSubmit={async (values) => {
        setSubmitError(null);
        try {
          await onSubmit(values);
        } catch {
          setSubmitError('Something went wrong. Please try again.');
        }
      }}
    >
      {formik => (
        <ModalFullPage open onClose={onClose} mobileOnly maxWidth="md" classes={{ paperWidthMd: classes.paper }}>
          <ModalTitle classes={{ title: classes.titleBgColor, root: classes.titleRoot }}>
            <Box display="flex" p={1}>
              <Box className={classes.titleText}>
                {l10n.providerSettings.onboarding.gettingStarted}
              </Box>
            </Box>
          </ModalTitle>
          <Divider />
          <ModalFullPageContent>
            {formik.isSubmitting ? (
              <Loader py={10} color="secondary" />
            ) : (
              <Box display="flex" flexDirection="column" gap="16px">
                <Box px={3}>
                  <Typography variant="h4" color="textPrimary">
                    Payer Enrollment
                  </Typography>
                  <Box mt={1}>
                    <Typography variant="body2" color="textSecondary">
                      Please ensure your information is correct to avoid delays processing payer enrollment applications.
                    </Typography>
                  </Box>
                </Box>
                <PayorEnrollmentFormContent elevated={false} />
                {submitError && (
                  <Typography variant="body2" color="error">{submitError}</Typography>
                )}
              </Box>
            )}
          </ModalFullPageContent>
          <Divider />
          <ModalActions>
            <CancelButton onClick={onClose} color="secondary">
              Cancel
            </CancelButton>
            <SubmitButton disabledOnInvalid color="secondary">
              Submit Enrollment
            </SubmitButton>
          </ModalActions>
        </ModalFullPage>
      )}
    </Formik>
  );
};

const useModalStyles = makeStyles()((theme) => ({
  paper: {
    maxWidth: '640px',
  },
  titleRoot: {
    padding: 0,
  },
  titleBgColor: {
    backgroundColor: theme.palette.grey[100],
  },
  titleText: {
    display: 'flex',
    flexGrow: 1,
    color: theme.palette.darkSlate.main,
    fontSize: '1rem',
    alignItems: 'center',
    marginLeft: theme.spacing(2),
    fontWeight: 600,
    letterSpacing: '0.5px',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.125rem',
      letterSpacing: '0.15px',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '1.125rem',
      justifyContent: 'center',
    },
  },
}));
